/**
 * Settings — `/settings`.
 *
 * Just the page shell: `sectionsForPage` says what's here and `renderSection` builds it,
 * the same pattern `Structure.tsx` uses — neither `DisplayCard` nor `TokensCard` (D110)
 * needs anything page-specific, so there is nothing left for this file to wire by hand.
 *
 * The `revision`/`bump` pair exists only for `LayoutGrid`'s `onSectionsChanged` (D114) — a
 * card moved onto this page via the unlock-mode picker needs *something* to force a
 * re-render so `sectionsForPage` gets asked again; neither card here has its own mutations
 * that would otherwise need it.
 */

import { useCallback, useState } from "react";
import { sectionsForPage, type SectionId } from "../layout/model";
import { renderSection } from "../layout/sectionRegistry";
import { LayoutGrid, type Section } from "../layout/LayoutGrid";

export function Settings() {
  const [revision, setRevision] = useState(0);
  const bump = useCallback(() => setRevision((n) => n + 1), []);

  const sections: Section[] = sectionsForPage("settings")
    .map((id): Section | null => {
      const node = renderSection(id as SectionId, { refreshKey: revision, onChange: bump });
      return node ? { id, node } : null;
    })
    .filter((section): section is Section => section !== null);

  return <LayoutGrid page="settings" sections={sections} onSectionsChanged={bump} />;
}
